import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { format, subDays } from 'date-fns';

import { areEqual } from 'Utils/equalityChecks';

import { createBulkExport, getBulkExports, cancelBulkExport, BulkExportStatus } from './actions';
import classes from './bulkExport.module.css';

const today = format(new Date(), 'yyyy-MM-dd');
const ninetyDaysAgo = format(subDays(new Date(), 90), 'yyyy-MM-dd');

const POLL_INTERVAL = 5000;

function BulkExport() {
  const [startDate, setStartDate] = useState(ninetyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [exports, setExports] = useState<BulkExportStatus[]>([]);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => () => { mountedRef.current = false; }, []);

  const fetchExports = useCallback(async () => {
    try {
      const data = await getBulkExports();
      if (mountedRef.current) setExports(data);
    } catch {
      // silent — polling failure is not critical
    }
  }, []);

  const hasActiveExport = exports.some((ex) => ex.status === 'pending' || ex.status === 'processing');

  // Poll while there's an active export
  useEffect(() => {
    fetchExports();
  }, [fetchExports]);

  useEffect(() => {
    if (hasActiveExport) {
      pollRef.current = setInterval(fetchExports, POLL_INTERVAL);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [hasActiveExport, fetchExports]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError('');
      setIsSubmitting(true);

      try {
        await createBulkExport(startDate, endDate);
        if (mountedRef.current) await fetchExports();
      } catch (err: any) {
        if (mountedRef.current) {
          const msg = err?.response?.data?.message;
          setError(msg && msg.length < 200 ? msg : 'Failed to start export. Please try again.');
        }
      } finally {
        if (mountedRef.current) setIsSubmitting(false);
      }
    },
    [startDate, endDate, fetchExports]
  );

  const handleCancel = useCallback(
    async (id: number) => {
      setCancellingId(id);
      try {
        await cancelBulkExport(id);
        if (mountedRef.current) await fetchExports();
      } catch {
        if (mountedRef.current) setError('Failed to cancel export.');
      } finally {
        if (mountedRef.current) setCancellingId(null);
      }
    },
    [fetchExports]
  );

  const formatStatus = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'processing':
        return 'Processing';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      default:
        return status;
    }
  };

  const statusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending':
      case 'processing':
        return 'bg-warning text-dark';
      case 'completed':
        return 'bg-success';
      case 'failed':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  };

  return (
    <div className={classes.card}>
      <h2 className={classes.title}>Bulk Data Export</h2>
      <p className={classes.description}>
        Export project data for the selected date range. You will receive a notification when the file is ready.
      </p>

      <form className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.fieldGroup}>
          <label className={classes.label} htmlFor="bulk-export-start">
            Start Date
          </label>
          <input
            id="bulk-export-start"
            type="date"
            className={`form-control ${classes.dateInput}`}
            value={startDate}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className={classes.fieldGroup}>
          <label className={classes.label} htmlFor="bulk-export-end">
            End Date
          </label>
          <input
            id="bulk-export-end"
            type="date"
            className={`form-control ${classes.dateInput}`}
            value={endDate}
            min={startDate}
            max={today}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <button
          type="submit"
          className={`btn btn-primary ${classes.exportButton}`}
          disabled={isSubmitting || hasActiveExport || !startDate || !endDate}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
              Exporting...
            </>
          ) : (
            'Export'
          )}
        </button>
      </form>

      {error && (
        <div className={`alert alert-danger ${classes.alert}`} role="alert">
          {error}
        </div>
      )}

      {exports.length > 0 && (
        <div className={classes.exportList}>
          <h3 className={classes.listTitle}>Recent Exports</h3>
          {exports.map((ex) => (
            <div key={ex.id} className={classes.exportRow}>
              <div className={classes.exportInfo}>
                <span className={`badge ${statusBadgeClass(ex.status)} ${classes.badge}`}>
                  {formatStatus(ex.status)}
                </span>
                <span className={classes.exportMeta}>
                  {ex.project_count} project{ex.project_count !== 1 ? 's' : ''}
                  {' — '}
                  {format(new Date(ex.created_at), 'MMM d, yyyy h:mm a')}
                </span>
              </div>
              <div className={classes.exportActions}>
                {ex.status === 'completed' && ex.download_url && (
                  <a href={ex.download_url} className="btn btn-sm btn-outline-success" download>
                    Download
                  </a>
                )}
                {(ex.status === 'pending' || ex.status === 'processing') && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleCancel(ex.id)}
                    disabled={cancellingId === ex.id}
                  >
                    {cancellingId === ex.id ? 'Cancelling...' : 'Cancel'}
                  </button>
                )}
                {ex.status === 'failed' && ex.error_message && (
                  <span className={classes.errorText}>{ex.error_message}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const BulkExportMemo = memo(BulkExport, areEqual);

export { BulkExportMemo as BulkExportContainer };
